import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Check, MoreHorizontal, Plus, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { listsByFamilyAtom } from '../store';
import {
  useAuth,
  useFamily,
  useNavAction,
  useOnKeysArrived,
  useLoadingState,
} from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { api, ApiError, type FamilyList, type ListTemplate } from '../lib/api';
import { NoFamilySelected } from '../components/family/NoFamilySelected';
import { ConfirmSheet } from '../components/ConfirmSheet';
import { EmptyState } from '../components/EmptyState';
import { isE2EEReady, isLockedApp } from '../lib/e2ee/session';
import { createSealedList } from '../lib/e2ee/apps';
import { ProgressBar } from '../components/ProgressBar';
import { appLocale } from '../lib/format';
import { t as translateNow, useT } from '../i18n';
import { isUnbound, UnboundMark } from '../components/UnboundMark';

type Translate = ReturnType<typeof useT>;

// "10 min ago", "Tue", "4 Oct".
function updatedAgo(iso: string, t: Translate, now = new Date()): string {
  const date = new Date(iso);
  const minutes = Math.round((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return t('apps.lists.justNow');
  if (minutes < 60) return t('apps.lists.minutesAgo', { count: minutes });
  if (minutes < 24 * 60 && date.toDateString() === now.toDateString()) {
    return t('apps.lists.hoursAgo', { count: Math.round(minutes / 60) });
  }
  if (minutes < 7 * 24 * 60)
    return date.toLocaleDateString(appLocale(), { weekday: 'short' });
  return date.toLocaleDateString(appLocale(), {
    day: 'numeric',
    month: 'short',
  });
}

// Lists (Living Room): each list as a card with its progress; finished
// lists rest quietly at the bottom.
export function ListsPage() {
  const { currentFamilyId, currentRole, families } = useFamily();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const [listsMap, setListsMap] = useAtom(listsByFamilyAtom);
  // True from the start when there is a family to ask: the empty state
  // must never flash before the loader's empty area (M66, D248).
  const [loading, setLoading] = useState(Boolean(currentFamilyId));
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<FamilyList | null>(null);
  const createDialog = useDisclosure();
  // Saved lists to start from (M26, D48).
  const [templates, setTemplates] = useState<ListTemplate[]>([]);
  const [templateId, setTemplateId] = useState<string | null>(null);
  useEffect(() => {
    if (!createDialog.isOpen || !currentFamilyId) return;
    setTemplateId(null);
    api
      .getListTemplates(currentFamilyId)
      // Only templates this device can open: a locked one's items would be
      // copied unread, and its name is a placeholder (#82).
      .then((response) =>
        setTemplates(response.data.filter((entry) => !isLockedApp(entry)))
      )
      .catch(() => setTemplates([]));
  }, [createDialog.isOpen, currentFamilyId]);

  const removeTemplate = async (template: ListTemplate) => {
    if (!currentFamilyId) return;
    try {
      await api.deleteListTemplate(currentFamilyId, template.id);
      setTemplates((current) =>
        current.filter((entry) => entry.id !== template.id)
      );
      if (templateId === template.id) setTemplateId(null);
    } catch (err) {
      toast({
        title: t('apps.lists.removeTemplateFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    }
  };

  const familyName =
    families.find((f) => f.id === currentFamilyId)?.name ??
    t('apps.shared.theFamily');
  const lists = currentFamilyId ? (listsMap[currentFamilyId] ?? []) : [];
  const canModerate = currentRole === 'root' || currentRole === 'admin';
  const isDone = (list: FamilyList) =>
    list.itemCount > 0 && list.checkedCount === list.itemCount;
  const open = lists.filter((list) => !isDone(list));
  const finished = lists.filter(isDone);

  // On an empty page the "New list" button is in the content instead.
  useNavAction(
    lists.length === 0 ? null : t('apps.lists.newList'),
    createDialog.onOpen
  );

  const load = useCallback(
    async (familyId: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getFamilyLists(familyId);
        setListsMap((current) => ({ ...current, [familyId]: response.data }));
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : translateNow('apps.lists.loadFailed')
        );
      } finally {
        setLoading(false);
      }
    },
    [setListsMap]
  );

  // The mark while the lists load (M66, D248).
  const listsState = useLoadingState({
    loading,
    hasData: lists.length > 0,
    error,
    retry: () => {
      if (currentFamilyId) void load(currentFamilyId);
    },
  });
  useEffect(() => {
    if (!currentFamilyId) return;
    void load(currentFamilyId);
  }, [currentFamilyId, load]);
  // The family's key reached this device: open what was locked (M28).
  useOnKeysArrived('family', currentFamilyId, () => {
    if (currentFamilyId) void load(currentFamilyId);
  });

  const handleCreate = async () => {
    if (!currentFamilyId || !name.trim()) return;
    setCreating(true);
    try {
      // The name is sealed for the family when this device can (M28, D65).
      const response = isE2EEReady()
        ? await createSealedList(
            currentFamilyId,
            name.trim(),
            templates.find((template) => template.id === templateId)
          )
        : await api.createFamilyList(
            currentFamilyId,
            name.trim(),
            templateId ?? undefined
          );
      setListsMap((current) => ({
        ...current,
        [currentFamilyId]: [response.data, ...(current[currentFamilyId] ?? [])],
      }));
      setName('');
      createDialog.onClose();
      navigate(`/lists/${response.data.id}`);
    } catch (err) {
      toast({
        title: t('apps.lists.createFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = async () => {
    const list = deleting;
    if (!currentFamilyId || !list) return;
    try {
      await api.deleteFamilyList(currentFamilyId, list.id);
      setListsMap((current) => ({
        ...current,
        [currentFamilyId]: (current[currentFamilyId] ?? []).filter(
          (item) => item.id !== list.id
        ),
      }));
      toast({ title: t('apps.lists.deleted'), status: 'success' });
    } catch (err) {
      toast({
        title: t('apps.lists.deleteFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setDeleting(null);
    }
  };

  if (!currentFamilyId) return <NoFamilySelected />;

  const menu = (list: FamilyList) =>
    (list.createdBy === currentUser?.id || canModerate) && (
      <Menu placement="bottom-end" isLazy>
        <MenuButton
          as={IconButton}
          aria-label={t('apps.shared.moreFor', { name: list.name })}
          icon={<MoreHorizontal size={18} />}
          size="sm"
          variant="ghost"
          colorScheme="gray"
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
        />
        <MenuList onClick={(event) => event.stopPropagation()}>
          <MenuItem
            icon={<Trash2 size={16} />}
            color="danger"
            onClick={() => setDeleting(list)}
          >
            {t('apps.lists.deleteMenu')}
          </MenuItem>
        </MenuList>
      </Menu>
    );

  return (
    <Box h="full" overflowY="auto" sx={{ overscrollBehaviorY: 'contain' }}>
      <Box maxW="900px" mx="auto" px={4} pb={6}>
        {error && !listsState.failed && (
          <Alert status="error" borderRadius="xl" mb={3}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {listsState.show ? (
          <LoadingPanel state={listsState} label={t('apps.lists.loading')} />
        ) : lists.length === 0 ? (
          <EmptyState
            kind="lists"
            title={t('apps.lists.emptyTitle')}
            body={t('apps.lists.emptyBody', { family: familyName })}
            action={{
              label: t('apps.lists.newList'),
              icon: <Plus size={20} />,
              onClick: createDialog.onOpen,
            }}
          />
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px">
            {open.map((list) => (
              <Box
                key={list.id}
                as="button"
                type="button"
                textAlign="left"
                onClick={() => navigate(`/lists/${list.id}`)}
                bg="surface"
                borderWidth="1px"
                borderColor="line"
                borderRadius="card"
                p={4}
                _hover={{ borderColor: 'accent' }}
              >
                <VStack align="stretch" spacing="10px">
                  <HStack spacing={2}>
                    <Text
                      as="span"
                      flex="1"
                      fontFamily="heading"
                      fontWeight={700}
                      fontSize="21px"
                      noOfLines={1}
                    >
                      {list.name}
                    </Text>
                    <Text
                      as="span"
                      fontSize="14px"
                      fontWeight={700}
                      color="accent"
                      flexShrink={0}
                    >
                      {t('apps.lists.progressCount', {
                        checked: list.checkedCount,
                        total: list.itemCount,
                      })}
                    </Text>
                  </HStack>
                  <ProgressBar
                    done={list.checkedCount}
                    total={list.itemCount}
                    label={t('apps.lists.progressLabel', { name: list.name })}
                  />
                  <HStack spacing={2}>
                    <Text
                      as="span"
                      flex="1"
                      fontSize="14px"
                      color="muted"
                      noOfLines={1}
                    >
                      {t('apps.lists.updated', {
                        name:
                          list.createdBy === currentUser?.id
                            ? t('common.you')
                            : (list.createdByUser?.displayName.split(' ')[0] ??
                              t('apps.shared.family')),
                        when: updatedAgo(list.updatedAt, t),
                      })}
                    </Text>
                    {menu(list)}
                  </HStack>
                </VStack>
              </Box>
            ))}
            {finished.map((list) => (
              <HStack
                key={list.id}
                as="button"
                type="button"
                textAlign="left"
                onClick={() => navigate(`/lists/${list.id}`)}
                bg="surface.subtle"
                borderRadius="card"
                p={4}
                spacing={3}
              >
                <Box
                  w="32px"
                  h="32px"
                  borderRadius="full"
                  bg="saffron"
                  color="#1F1D1B"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  aria-hidden="true"
                >
                  <Check size={18} strokeWidth={3} />
                </Box>
                <Box flex="1" minW={0}>
                  <Text
                    fontFamily="heading"
                    fontWeight={700}
                    fontSize="19px"
                    noOfLines={1}
                  >
                    {list.name}
                  </Text>
                  {isUnbound(list) && <UnboundMark record />}
                  <Text fontSize="14px" color="muted">
                    {t('apps.lists.allDone', { count: list.itemCount })}
                  </Text>
                </Box>
                {menu(list)}
              </HStack>
            ))}
          </SimpleGrid>
        )}
      </Box>

      <Modal
        isOpen={createDialog.isOpen}
        onClose={createDialog.onClose}
        isCentered
      >
        <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
        <ModalContent mx={4} borderRadius="28px">
          <ModalHeader>
            <Heading as="h2" fontSize="24px">
              {t('apps.lists.newList')}
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {templates.length > 0 && (
              <Box mb={4}>
                <Text fontWeight={600} mb={2}>
                  {t('apps.lists.startFrom')}
                </Text>
                <Wrap spacing={2}>
                  <WrapItem>
                    <Button
                      h="36px"
                      px={3}
                      fontSize="14px"
                      colorScheme="gray"
                      bg={templateId === null ? 'ink' : 'surface'}
                      color={templateId === null ? 'canvas' : 'ink'}
                      borderWidth={templateId === null ? 0 : '1px'}
                      borderColor="line"
                      aria-pressed={templateId === null}
                      onClick={() => setTemplateId(null)}
                    >
                      {t('apps.lists.blank')}
                    </Button>
                  </WrapItem>
                  {templates.map((template) => {
                    const selected = templateId === template.id;
                    return (
                      <WrapItem key={template.id}>
                        <HStack
                          spacing={0}
                          h="36px"
                          borderRadius="full"
                          bg={selected ? 'ink' : 'surface'}
                          color={selected ? 'canvas' : 'ink'}
                          borderWidth={selected ? 0 : '1px'}
                          borderColor="line"
                          overflow="hidden"
                        >
                          <Box
                            as="button"
                            type="button"
                            px={3}
                            h="full"
                            fontSize="14px"
                            fontWeight={600}
                            aria-pressed={selected}
                            onClick={() => {
                              setTemplateId(template.id);
                              if (!name.trim()) setName(template.name);
                            }}
                          >
                            {template.name} · {template.items.length}
                          </Box>
                          <IconButton
                            aria-label={t('apps.lists.removeTemplate', {
                              name: template.name,
                            })}
                            icon={<X size={14} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="gray"
                            color="inherit"
                            mr={1}
                            onClick={() => void removeTemplate(template)}
                          />
                        </HStack>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              </Box>
            )}
            <FormControl>
              <FormLabel>{t('apps.lists.name')}</FormLabel>
              <Input
                autoFocus
                placeholder={t('apps.lists.namePlaceholder')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void handleCreate();
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              colorScheme="gray"
              onClick={createDialog.onClose}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => void handleCreate()}
              isLoading={creating}
              isDisabled={!name.trim()}
            >
              {t('apps.lists.create')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmSheet
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title={t('apps.lists.deleteTitle', { name: deleting?.name ?? '' })}
        body={t(
          deleting?.itemCount === 1
            ? 'apps.lists.deleteBodyOne'
            : 'apps.lists.deleteBodyOther',
          { count: deleting?.itemCount ?? 0, family: familyName }
        )}
        confirmLabel={t('apps.lists.deleteConfirm')}
        confirmIcon={<Trash2 size={18} />}
        cancelLabel={t('apps.shared.keepIt')}
        onConfirm={() => void confirmDelete()}
      />
    </Box>
  );
}
